import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { ApiError } from '@proptr/api-client';
import { Turnstile } from '@proptr/ui';
import {
  AlertIcon,
  ArrowIcon,
  AuthShell,
} from '../components/layout/AuthShell.js';
import { client } from '../main.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

type Banner = { tone: 'error' | 'warn'; title?: string; message: string };

export function LoginPage() {
  const navigate = useNavigate();
  // Set by /accept-invite: the new admin lands here with their email
  // filled in and a note that the password is saved.
  const arrival = (useLocation().state ?? null) as { email?: string; notice?: string } | null;
  const errorRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState(arrival?.email ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Bumped after every failed attempt: the API spends the token either way.
  const [turnstileReset, setTurnstileReset] = useState(0);

  const normalizedEmail = email.trim();
  const formValid =
    EMAIL_PATTERN.test(normalizedEmail) &&
    password.length > 0 &&
    (!TURNSTILE_SITE_KEY || Boolean(turnstileToken));

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!formValid || loading) return;
    setBanner(null);
    setLoading(true);
    try {
      const res = await client.auth.adminLogin({
        email: normalizedEmail,
        password,
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      // Carry the OTP token + masked email to /verify via route state so
      // neither ends up in the URL.
      navigate('/verify', {
        replace: true,
        state: {
          otpToken: res.otpToken,
          maskedEmail: res.maskedEmail,
          email: normalizedEmail,
        },
      });
    } catch (err) {
      setBanner(bannerFor(err));
      setTurnstileReset((n) => n + 1);
      requestAnimationFrame(() => errorRef.current?.focus());
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      <form className="auth-form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <h1>Sign in</h1>
        <p className="auth-subtitle">Pick up where the network left off.</p>

        {!banner && arrival?.notice && (
          <div className="auth-banner is-success" role="status">
            <AlertIcon />
            <p>{arrival.notice}</p>
          </div>
        )}

        {banner && (
          <div
            ref={errorRef}
            className={banner.tone === 'warn' ? 'auth-banner is-warn' : 'auth-banner'}
            role="alert"
            tabIndex={-1}
          >
            <AlertIcon />
            <p>
              {banner.title && <strong>{banner.title} </strong>}
              {banner.message}
            </p>
          </div>
        )}

        <label htmlFor="email">Email address</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          placeholder="you@proptr.ee"
          value={email}
          disabled={loading}
          autoFocus={!arrival?.email}
          onChange={(e) => {
            setEmail(e.target.value);
            setBanner(null);
          }}
        />

        <div className="auth-password-label">
          <label htmlFor="password">Password</label>
          <a href="mailto:support@proptr.ee?subject=Admin%20password%20reset">Forgot?</a>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="auth-password-input"
            value={password}
            disabled={loading}
            autoFocus={!!arrival?.email}
            onChange={(e) => {
              setPassword(e.target.value);
              setBanner(null);
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
            style={{
              position: 'absolute',
              right: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              border: 0,
              background: 'none',
              cursor: 'pointer',
              color: '#9a9ab4',
              display: 'flex',
            }}
          >
            {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>

        {TURNSTILE_SITE_KEY && (
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            onToken={setTurnstileToken}
            resetKey={turnstileReset}
            className="auth-turnstile"
          />
        )}

        <button
          type="submit"
          className="auth-primary-button auth-login-button"
          disabled={!formValid || loading}
          aria-busy={loading}
        >
          {loading ? (
            <>
              <span className="auth-spinner" aria-hidden="true" /> Signing you in
            </>
          ) : (
            <>
              Log in <ArrowIcon />
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}

function bannerFor(err: unknown): Banner {
  if (err instanceof ApiError) {
    if (err.code === 'AUTH_NOT_ADMIN') {
      return {
        tone: 'warn',
        title: 'Admin access required.',
        message:
          'Your account exists but does not have Admin level. Use the manager portal if you have manager access.',
      };
    }
    if (err.code === 'AUTH_ACCOUNT_INACTIVE') {
      return {
        tone: 'warn',
        title: 'Account is not active.',
        message: 'Contact another admin to reactivate it.',
      };
    }
    if (err.status === 401) {
      return {
        tone: 'error',
        title: 'That didn’t work.',
        message: 'That email and password don’t match. Please try again.',
      };
    }
    return { tone: 'error', message: err.message || 'Sign in failed.' };
  }
  return {
    tone: 'error',
    message:
      'We couldn’t reach the sign-in service. Nothing is wrong with your account — please try again.',
  };
}
